import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ExportOutlined, FileOutlined, SwapOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import type { IFileMenuAnchor } from './Explorer';
import type { ITreeNode } from './types';

/**
 * Right-click menu on a file — the pairing verbs. "Compare with Selected" is
 * enabled only once another file has been picked via "Select for Compare",
 * which is exactly how a reviewer chooses the two documents.
 */

interface IFileMenuProps {
  anchor: IFileMenuAnchor;
  compareSelectId: string | null;
  onClose: () => void;
  onOpen: (f: ITreeNode) => void;
  onOpenToSide: (f: ITreeNode) => void;
  onSelectForCompare: (f: ITreeNode) => void;
  onCompareWithSelected: (f: ITreeNode) => void;
}

const FileMenu: React.FC<IFileMenuProps> = ({
  anchor,
  compareSelectId,
  onClose,
  onOpen,
  onOpenToSide,
  onSelectForCompare,
  onCompareWithSelected,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: anchor.x, top: anchor.y });
  const { file } = anchor;
  const fileId = file.fileId;
  const canCompare = !!compareSelectId && !!fileId && compareSelectId !== fileId;

  // Keep the menu on-screen.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      left: Math.min(anchor.x, window.innerWidth - r.width - 8),
      top: Math.min(anchor.y, window.innerHeight - r.height - 8),
    });
  }, [anchor]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  const act = (fn: (f: ITreeNode) => void) => () => {
    fn(file);
    onClose();
  };

  return (
    <Menu ref={ref} style={{ left: pos.left, top: pos.top }}>
      <Item onClick={act(onOpen)}>
        <FileOutlined /> Open
      </Item>
      <Item onClick={act(onOpenToSide)}>
        <ExportOutlined /> Open to the Side
      </Item>
      <Sep />
      <Item onClick={act(onSelectForCompare)}>
        <SwapOutlined /> Select for Compare
      </Item>
      <Item disabled={!canCompare} onClick={act(onCompareWithSelected)}>
        <SwapOutlined /> Compare with Selected
      </Item>
      <Note>
        {compareSelectId
          ? canCompare
            ? 'Compares this file against the selected one.'
            : 'This file is selected for compare.'
          : 'Pick a first file, then a second — that pair fills two groups.'}
      </Note>
    </Menu>
  );
};

export default FileMenu;

const Menu = styled.div`
  position: fixed;
  z-index: 1000;
  min-width: 220px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: 4px;
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 10px;
  border: none;
  background: transparent;
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.textPrimary};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.cardHover};
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.textDisabled};
    cursor: not-allowed;
  }
  &:disabled:hover {
    background: transparent;
  }
`;

const Sep = styled.div`
  height: 1px;
  margin: 4px 0;
  background: ${({ theme }) => theme.colors.borderLight};
`;

const Note = styled.div`
  padding: 6px 10px 4px;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 260px;
`;
